import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar,
} from "recharts";
import axios from "axios";
import API_BASE_URL from "../config";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import {
  FaClipboardCheck, FaChevronDown, FaCheckCircle,
  FaTasks, FaClock, FaExclamationTriangle,
  FaFire, FaBolt, FaTh, FaProjectDiagram,
} from "react-icons/fa";

// ─── Design tokens (same as Admin) ───────────────────────────
const T = {
  bg:       "#f8fafc",
  white:    "#ffffff",
  border:   "#e2e8f0",
  shadow:   "0 1px 3px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
  shadowMd: "0 4px 12px rgba(15,23,42,0.08)",
  radius:   12,
  text1:    "#0f172a",
  text2:    "#475569",
  text3:    "#94a3b8",
  primary:  "#6366f1",
};

const OPEN_STATUSES = ["PENDING", "IN_PROGRESS", "READY TO REVIEW", "READY TO TEST", "IN TESTING"];
const DASH_TABS     = ["Overview", "Weekly Trends"];
const TIME_OPTS     = ["All", "6M", "3M", "1M", "1W"];
const TIME_DAYS     = { "6M": 180, "3M": 90, "1M": 30, "1W": 7 };

const STATUS_COLORS = {
  PENDING:           "#f59e0b",
  IN_PROGRESS:       "#3b82f6",
  "READY TO REVIEW": "#8b5cf6",
  "READY TO TEST":   "#f97316",
  "IN TESTING":      "#06b6d4",
  COMPLETED:         "#10b981",
  CANCELLED:         "#9ca3af",
  REVIEW:            "#8b5cf6",
};
const PRIORITY_COLORS = { High: "#ef4444", Medium: "#f59e0b", Low: "#10b981" };
const TYPE_COLORS     = ["#6366f1","#8b5cf6","#ec4899","#f43f5e","#f59e0b","#10b981","#06b6d4","#3b82f6"];

const STATUS_BADGE = {
  PENDING:           { bg: "#fffbeb", color: "#92400e" },
  IN_PROGRESS:       { bg: "#eff6ff", color: "#1d4ed8" },
  "READY TO REVIEW": { bg: "#f5f3ff", color: "#6d28d9" },
  "READY TO TEST":   { bg: "#fff7ed", color: "#c2410c" },
  "IN TESTING":      { bg: "#ecfeff", color: "#0e7490" },
  COMPLETED:         { bg: "#f0fdf4", color: "#166534" },
  CANCELLED:         { bg: "#f8fafc", color: "#64748b" },
  REVIEW:            { bg: "#f5f3ff", color: "#6d28d9" },
};
const PRIORITY_BADGE = {
  High:   { bg: "#fef2f2", color: "#dc2626" },
  Medium: { bg: "#fffbeb", color: "#b45309" },
  Low:    { bg: "#f0fdf4", color: "#166534" },
};

// ─── Normalizers (fix inconsistent casing / typos from API) ──
const PRIORITY_NORM = { HIGH: "High", MEDIUM: "Medium", LOW: "Low" };
function normalizePriority(p) {
  if (!p) return "None";
  return PRIORITY_NORM[p.toUpperCase()] || p;
}
const STATUS_NORM = { "REAY TO TEST": "READY TO TEST", "REVIEW": "READY TO REVIEW" };
function normalizeStatus(s) {
  if (!s) return s;
  const t = s.trim();
  return STATUS_NORM[t] || t;
}

// ─── Helpers ──────────────────────────────────────────────────
function isoWeekLabel(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dn = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dn);
  const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return `${d.getUTCFullYear()}-W${String(Math.ceil(((d - ys) / 86400000 + 1) / 7)).padStart(2, "0")}`;
}
function buildWeekBuckets(n) {
  const now = new Date(), buckets = {}, order = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(d.getDate() - i * 7);
    const lb = isoWeekLabel(d);
    if (!(lb in buckets)) { buckets[lb] = null; order.push(lb); }
  }
  return { buckets, order };
}

// ─── Micro-components ─────────────────────────────────────────
const SLabel = ({ children }) => (
  <span style={{ fontSize: "0.65rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
    {children}
  </span>
);

const StatCard = ({ label, value, sub, accent, Icon }) => (
  <div style={{
    background: T.white, borderRadius: T.radius, padding: "18px 20px 16px",
    boxShadow: T.shadow, borderTop: `3px solid ${accent}`, height: "100%",
    transition: "box-shadow 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowMd}
    onMouseLeave={e => e.currentTarget.style.boxShadow = T.shadow}
  >
    <div className="d-flex align-items-start justify-content-between mb-2">
      <SLabel>{label}</SLabel>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color={accent} />
      </div>
    </div>
    <div style={{ fontSize: "2rem", fontWeight: 800, color: T.text1, lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: "0.73rem", color: T.text3 }}>{sub}</div>
  </div>
);

const Chip = ({ children, bg, color }) => (
  <span style={{ background: bg, color, borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, display: "inline-block" }}>
    {children}
  </span>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: T.white, borderRadius: T.radius, padding: "20px 24px", boxShadow: T.shadow, ...style }}>
    {children}
  </div>
);

const CardHead = ({ label, right }) => (
  <div className="d-flex align-items-center justify-content-between mb-4">
    <SLabel>{label}</SLabel>
    {right}
  </div>
);

const ttStyle = {
  contentStyle: { fontSize: 12, borderRadius: 8, border: `1px solid ${T.border}`, boxShadow: T.shadowMd, padding: "8px 12px", color: T.text2 },
  labelStyle:   { fontWeight: 700, color: T.text1, marginBottom: 2 },
};

const PillSelect = ({ value, onChange, options, active }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const selected = options.find(o => o.value === value) || options[0];
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(v => !v)} style={{
        display: "flex", alignItems: "center", gap: 6, padding: "5px 10px 5px 12px", borderRadius: 6,
        border: `1.5px solid ${active ? T.primary : T.border}`,
        background: active ? "#eef2ff" : T.white, color: active ? T.primary : T.text2,
        fontSize: "0.78rem", fontWeight: active ? 600 : 500, cursor: "pointer", outline: "none",
        whiteSpace: "nowrap", boxShadow: open ? `0 0 0 3px ${T.primary}22` : "none", transition: "all 0.15s",
      }}>
        <span>{selected?.label}</span>
        <FaChevronDown size={9} style={{ color: active ? T.primary : T.text3, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: Math.max(ref.current?.offsetWidth || 0, 160), maxHeight: 260, overflowY: "auto",
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: "0 8px 24px rgba(15,23,42,0.13), 0 2px 8px rgba(15,23,42,0.06)",
          zIndex: 9999, padding: "5px",
        }}>
          {options.map(opt => {
            const isSel = opt.value === value;
            return (
              <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: "7px 10px", borderRadius: 6, fontSize: "0.8rem",
                fontWeight: isSel ? 600 : 400, color: isSel ? T.primary : T.text1,
                background: isSel ? "#eef2ff" : "transparent", cursor: "pointer",
                transition: "background 0.1s", whiteSpace: "nowrap",
              }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = T.bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSel ? "#eef2ff" : "transparent"; }}
              >
                <span>{opt.label}</span>
                {isSel && <FaCheckCircle size={10} color={T.primary} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
const EmployeeTaskLanding = () => {
  const navigate    = useNavigate();
  const employeeId  = localStorage.getItem("employeeId");

  const [tasks,       setTasks]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeDashTab, setActiveDashTab] = useState("Overview");
  const [tabSwitching,  setTabSwitching]  = useState(false);
  const [timeRange,     setTimeRange]     = useState("All");
  const [filterProject, setFilterProject] = useState("");
  const [weekTypeFilter, setWeekTypeFilter] = useState("All");

  const switchDashTab = useCallback((tab) => {
    setTabSwitching(true); setActiveDashTab(tab);
    setTimeout(() => setTabSwitching(false), 180);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!employeeId) { toast.error("Employee profile not found."); setLoading(false); return; }
    axios.get(`${API_BASE_URL}/api/tasks/projects-of-employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setTasks(res.data.map(t => ({
        ...t,
        priority: normalizePriority(t.priority),
        status: normalizeStatus(t.status),
      }))))
      .catch(() => toast.error("Unable to load task data"))
      .finally(() => setLoading(false));
  }, [navigate, employeeId]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const projectOptions = useMemo(() => {
    const s = new Set(tasks.filter(t => t.projectName).map(t => t.projectName));
    return [{ value: "", label: "All Projects" }, ...[...s].sort().map(p => ({ value: p, label: p }))];
  }, [tasks]);

  // All project tasks filtered by time + project
  const filteredTasks = useMemo(() => {
    let d = tasks;
    if (timeRange !== "All") {
      const cut = new Date(today); cut.setDate(cut.getDate() - TIME_DAYS[timeRange]);
      d = d.filter(t => t.createdAt && new Date(t.createdAt) >= cut);
    }
    if (filterProject) d = d.filter(t => t.projectName === filterProject);
    return d;
  }, [tasks, timeRange, filterProject, today]);

  // MY tasks only (assigned to logged-in employee)
  const myTasks = useMemo(() =>
    filteredTasks.filter(t => String(t.assigneeId) === String(employeeId)),
  [filteredTasks, employeeId]);

  // Derived stats — focused on MY tasks
  const D = useMemo(() => {
    const total     = myTasks.length;
    const doneList  = myTasks.filter(t => t.status === "COMPLETED");
    const completed = doneList.length;
    const open      = myTasks.filter(t => OPEN_STATUSES.includes(t.status)).length;
    const rate      = total > 0 ? Math.round((completed / total) * 100) : 0;
    const overdue   = myTasks.filter(t => t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;
    const highPri   = myTasks.filter(t => t.priority === "High" && OPEN_STATUSES.includes(t.status)).length;
    const critical  = myTasks.filter(t => t.priority === "High" && t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;

    // Charts — MY tasks
    const statusData   = Object.entries(myTasks.reduce((a,t)=>{ a[t.status]=(a[t.status]||0)+1; return a; },{})).map(([name,value])=>({name,value}));
    const priorityData = Object.entries(myTasks.reduce((a,t)=>{ const p=t.priority||"None"; a[p]=(a[p]||0)+1; return a; },{})).map(([name,value])=>({name,value}));
    const typeData     = Object.entries(myTasks.reduce((a,t)=>{ const tp=t.taskType||"General"; a[tp]=(a[tp]||0)+1; return a; },{})).map(([name,value])=>({name,value}));

    // Weekly volume (my tasks)
    const { buckets: waB, order: waO } = buildWeekBuckets(14);
    waO.forEach(w => { waB[w] = 0; });
    myTasks.forEach(t => { if (!t.createdAt) return; const lb=isoWeekLabel(new Date(t.createdAt)); if (lb in waB) waB[lb]++; });
    const weeklyData = waO.map(w => ({ week: w, tasks: waB[w] }));

    // Weekly by type
    const { buckets: wtB, order: wtO } = buildWeekBuckets(12);
    const typeSet = new Set();
    wtO.forEach(w => { wtB[w] = {}; });
    myTasks.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wtB)) return;
      const tp = t.taskType || "General"; typeSet.add(tp);
      wtB[lb][tp] = (wtB[lb][tp] || 0) + 1;
    });
    const weeklyByType = wtO.map(w => ({ week: w, ...wtB[w] }));
    const allTypes = [...typeSet];

    // Weekly by priority
    const { buckets: wpB, order: wpO } = buildWeekBuckets(12);
    wpO.forEach(w => { wpB[w] = { High:0, Medium:0, Low:0 }; });
    myTasks.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wpB)) return;
      const p = t.priority || "Low"; if (wpB[lb][p] !== undefined) wpB[lb][p]++;
    });
    const weeklyByPriority = wpO.map(w => ({ week: w, ...wpB[w] }));

    // Aging
    const ago30 = new Date(today); ago30.setDate(ago30.getDate()-30);
    const agingCount = myTasks.filter(t => OPEN_STATUSES.includes(t.status) && t.createdAt && new Date(t.createdAt) < ago30).length;

    // Upcoming due (next 7 days)
    const next7 = new Date(today); next7.setDate(next7.getDate()+7);
    const upcoming = myTasks.filter(t => OPEN_STATUSES.includes(t.status) && t.dueDate && new Date(t.dueDate) >= today && new Date(t.dueDate) <= next7)
      .sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate));

    // Project context (all tasks in my projects for awareness)
    const projectCount = new Set(filteredTasks.map(t => t.projectName).filter(Boolean)).size;

    return { total, open, completed, rate, overdue, highPri, critical, agingCount, upcoming, projectCount, statusData, priorityData, typeData, weeklyData, weeklyByType, allTypes, weeklyByPriority };
  }, [myTasks, filteredTasks, today]);

  const filteredWeeklyByType = useMemo(() => {
    if (weekTypeFilter === "All") return D.weeklyByType;
    return D.weeklyByType.map(w => ({ week: w.week, [weekTypeFilter]: w[weekTypeFilter]||0 }));
  }, [D.weeklyByType, weekTypeFilter]);

  // My open tasks sorted by priority + due date for the task list
  const myOpenTasks = useMemo(() => myTasks
    .filter(t => OPEN_STATUSES.includes(t.status))
    .sort((a,b) => {
      const po = { High:0, Medium:1, Low:2 };
      const diff = (po[a.priority]??3) - (po[b.priority]??3);
      return diff!==0 ? diff : (!a.dueDate?1:!b.dueDate?-1:new Date(a.dueDate)-new Date(b.dueDate));
    }),
  [myTasks]);

  const statCards = [
    { label: "My Open Tasks",  value: D.open,      sub: "Currently assigned to me",  accent: "#ea580c", Icon: FaTasks          },
    { label: "Completed",      value: D.completed, sub: `${D.rate}% completion rate`, accent: T.primary, Icon: FaCheckCircle    },
    { label: "Overdue",        value: D.overdue,   sub: "Past due — needs attention", accent: "#dc2626", Icon: FaExclamationTriangle },
    { label: "High Priority",  value: D.highPri,   sub: "Open high-priority tasks",   accent: "#dc2626", Icon: FaFire           },
    { label: "Critical",       value: D.critical,  sub: "Overdue + high priority",    accent: "#9f1239", Icon: FaBolt           },
    { label: "Due This Week",  value: D.upcoming.length, sub: "Open tasks due in 7 days", accent: "#7c3aed", Icon: FaClock      },
    { label: "Total My Tasks", value: D.total,     sub: "Assigned to me (all time)",  accent: "#475569", Icon: FaTasks          },
    { label: "My Projects",    value: D.projectCount, sub: "Projects I'm part of",   accent: "#0284c7", Icon: FaProjectDiagram },
  ];

  const axisProps = { axisLine: false, tickLine: false };
  const xTick = { fontSize: 10, fill: T.text3 };
  const yTick = { fontSize: 10, fill: T.text3 };
  const anyFilter = timeRange !== "All" || filterProject;

  const NAVBAR_H    = 105;
  const SUBNAV_H    = 44;
  const FILTERBAR_H = 44;
  const DASHTABS_H  = 40;

  const donutChart = ({ label, data, colorFn }) => (
    <div key={label} className="col-12 col-md-4">
      <Card style={{ height:"100%" }}>
        <CardHead label={label} />
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="38%" innerRadius={48} outerRadius={76} paddingAngle={3}>
              {data.map((e,i) => <Cell key={i} fill={colorFn(e,i)} />)}
            </Pie>
            <Tooltip {...ttStyle} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize:"0.72rem", lineHeight:"1.9", paddingLeft:8, color:T.text2 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );

  return (
    <>
      <div className="fixed-top"><EmployeeNavbar /></div>

      <div style={{ paddingTop: NAVBAR_H, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ════ SUB-NAV ════ */}
        <div style={{ flexShrink:0, background:T.white, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px", height:SUBNAV_H }}>
          <div className="d-flex align-items-center" style={{ gap:2 }}>
            <button style={{
              display:"flex", alignItems:"center", gap:6, padding:`0 14px`, height:SUBNAV_H,
              fontSize:"0.8rem", fontWeight:700, color:T.primary, background:"#eef2ff",
              border:"none", borderBottom:`2px solid ${T.primary}`, cursor:"default", whiteSpace:"nowrap",
            }}>
              <FaTh size={12} /> My Dashboard
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/viewtasks" style={{
              display:"flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6,
              fontSize:"0.75rem", fontWeight:600, background:T.primary, color:"#fff",
              textDecoration:"none", whiteSpace:"nowrap", transition:"opacity 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity="0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity="1"}
            >
              <FaClipboardCheck size={11} /> Tasks Board
            </Link>
          </div>
        </div>

        {/* ════ FILTER BAR ════ */}
        <div style={{ flexShrink:0, background:T.white, borderBottom:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.05)" }}>
          <div style={{ padding:"0 24px" }}>
            <div className="d-flex align-items-center justify-content-between" style={{ height:FILTERBAR_H, gap:12 }}>
              <div className="d-flex align-items-center gap-2" style={{ flexWrap:"wrap" }}>
                <span style={{ fontSize:"0.68rem", fontWeight:800, color:T.text3, textTransform:"uppercase", letterSpacing:"0.1em", marginRight:6 }}>
                  My Tasks
                </span>
                {projectOptions.length > 1 && (
                  <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
                )}
                {anyFilter && (
                  <button onClick={() => { setTimeRange("All"); setFilterProject(""); }}
                    style={{ fontSize:"0.72rem", color:T.text3, background:"none", border:"none", cursor:"pointer", textDecoration:"underline", padding:0 }}>
                    Reset
                  </button>
                )}
              </div>
              <div style={{ display:"inline-flex", border:`1.5px solid ${T.border}`, borderRadius:6, overflow:"hidden", flexShrink:0 }}>
                {TIME_OPTS.map((opt,i) => (
                  <button key={opt} onClick={() => setTimeRange(opt)} style={{
                    padding:"4px 12px", fontSize:"0.75rem", border:"none",
                    borderLeft:i>0?`1px solid ${T.border}`:"none",
                    background:timeRange===opt?"#0f172a":"transparent",
                    color:timeRange===opt?"#fff":T.text3,
                    cursor:"pointer", fontWeight:timeRange===opt?700:500, transition:"all 0.12s",
                  }}>{opt}</button>
                ))}
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="d-flex" style={{ gap:0, borderTop:`1px solid ${T.border}` }}>
              {DASH_TABS.map(tab => {
                const isActive = activeDashTab===tab;
                return (
                  <button key={tab} onClick={() => switchDashTab(tab)} style={{
                    padding:`0 18px`, height:DASHTABS_H, fontSize:"0.82rem",
                    fontWeight:isActive?600:500, color:isActive?T.primary:T.text2,
                    background:"transparent", border:"none",
                    borderBottomWidth:2, borderBottomStyle:"solid", borderBottomColor:isActive?T.primary:"transparent",
                    cursor:"pointer", whiteSpace:"nowrap", transition:"color 0.15s, border-color 0.15s",
                  }}>{tab}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ════ SCROLLABLE CONTENT ════ */}
        <div className="emp-scroll" style={{ flex:1, overflowY:"auto", background:T.bg }}>

          {(loading || tabSwitching) ? (
            <div style={{ height:"100%", minHeight:420, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
              <div style={{ position:"relative", width:48, height:48 }}>
                <div className="spinner-border" style={{ width:48, height:48, color:T.primary, opacity:0.18, position:"absolute", top:0, left:0 }} />
                <div className="spinner-border" style={{ width:48, height:48, color:T.primary, position:"absolute", top:0, left:0, borderTopColor:"transparent", borderRightColor:"transparent" }} />
              </div>
              <div style={{ textAlign:"center" }}>
                <p style={{ fontSize:"0.92rem", fontWeight:600, color:T.text1, marginBottom:4 }}>
                  {loading ? "Loading your tasks" : `Opening ${activeDashTab}`}
                </p>
                <p style={{ fontSize:"0.78rem", color:T.text3, marginBottom:0 }}>
                  {loading ? "Fetching your assigned tasks…" : "Preparing view…"}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding:"20px 24px" }}>

              {anyFilter && (
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {timeRange!=="All" && <span style={{ fontSize:"0.72rem", background:"#eef2ff", color:T.primary, borderRadius:20, padding:"3px 12px", fontWeight:600, border:`1px solid #c7d2fe` }}>Last {timeRange}</span>}
                  {filterProject && <span style={{ fontSize:"0.72rem", background:"#eef2ff", color:T.primary, borderRadius:20, padding:"3px 12px", fontWeight:600, border:`1px solid #c7d2fe` }}>{filterProject}</span>}
                  <span style={{ fontSize:"0.72rem", color:T.text3, padding:"3px 0" }}>{myTasks.length} of {tasks.filter(t=>String(t.assigneeId)===String(employeeId)).length} my tasks</span>
                </div>
              )}

              {/* ── OVERVIEW ── */}
              {activeDashTab === "Overview" && (
                <>
                  {/* Stat cards */}
                  <div className="row g-3 mb-4">
                    {statCards.map((c,i) => (
                      <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>
                    ))}
                  </div>

                  {/* Volume area chart */}
                  <Card style={{ marginBottom:20 }}>
                    <CardHead label="My Task Volume by Week"
                      right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>{myTasks.length} tasks assigned · {D.weeklyData.length} weeks</span>}
                    />
                    <ResponsiveContainer width="100%" height={160}>
                      <AreaChart data={D.weeklyData} margin={{ top:4, right:8, left:-24, bottom:0 }}>
                        <defs>
                          <linearGradient id="empGrad1" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={T.primary} stopOpacity={0.15} />
                            <stop offset="95%" stopColor={T.primary} stopOpacity={0}    />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                        <XAxis dataKey="week" tick={xTick} {...axisProps} />
                        <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                        <Tooltip {...ttStyle} />
                        <Area type="monotone" dataKey="tasks" name="My Tasks" stroke={T.primary} strokeWidth={2}
                          fill="url(#empGrad1)" dot={{ fill:T.primary, r:3, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Card>

                  {/* Donut charts */}
                  <div className="row g-3 mb-4">
                    {[
                      { label:"My Tasks by Status",   data:D.statusData,   colorFn:(e)   => STATUS_COLORS[e.name]   || "#94a3b8" },
                      { label:"My Tasks by Priority", data:D.priorityData, colorFn:(e)   => PRIORITY_COLORS[e.name] || "#94a3b8" },
                      { label:"My Tasks by Type",     data:D.typeData,     colorFn:(_,i) => TYPE_COLORS[i%TYPE_COLORS.length]    },
                    ].map(donutChart)}
                  </div>

                  {/* Upcoming due + Open task list */}
                  <div className="row g-3">
                    <div className="col-12 col-md-5">
                      <Card style={{ height:"100%" }}>
                        <CardHead label="Due This Week"
                          right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>{D.upcoming.length} tasks</span>}
                        />
                        {D.upcoming.length===0
                          ? <p style={{ fontSize:"0.84rem", color:T.text3, lineHeight:1.7 }}>No tasks due in the next 7 days.</p>
                          : D.upcoming.map(t => {
                              const pb = PRIORITY_BADGE[t.priority] || { bg:T.bg, color:T.text2 };
                              const due = new Date(t.dueDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short"});
                              const daysLeft = Math.ceil((new Date(t.dueDate)-today)/86400000);
                              return (
                                <div key={t.id} style={{ borderBottom:`1px solid ${T.border}`, padding:"10px 0", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div style={{ fontWeight:600, color:T.text1, fontSize:"0.84rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.title}</div>
                                    <div style={{ fontSize:"0.71rem", color:T.text3, marginTop:2 }}>{t.projectName||"—"}</div>
                                  </div>
                                  <div className="d-flex flex-column align-items-end gap-1" style={{ flexShrink:0 }}>
                                    <span style={{ ...pb, borderRadius:20, padding:"2px 9px", fontSize:"0.69rem", fontWeight:700 }}>{t.priority}</span>
                                    <span style={{ fontSize:"0.72rem", fontWeight:600, color:daysLeft<=1?"#dc2626":daysLeft<=3?"#f59e0b":T.text2 }}>{due}</span>
                                  </div>
                                </div>
                              );
                            })
                        }
                        <Link to="/viewtasks" style={{ fontSize:"0.78rem", color:T.primary, fontWeight:600, textDecoration:"none", display:"block", marginTop:12 }}>
                          View all my tasks →
                        </Link>
                      </Card>
                    </div>

                    <div className="col-12 col-md-7">
                      <Card style={{ height:"100%" }}>
                        <CardHead label="My Open Tasks"
                          right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>{myOpenTasks.length} open</span>}
                        />
                        {myOpenTasks.length===0
                          ? <p style={{ fontSize:"0.84rem", color:T.text3 }}>No open tasks. Well done!</p>
                          : (
                            <div className="table-responsive">
                              <table className="table table-borderless align-middle mb-0">
                                <thead>
                                  <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                                    {["Task","Project","Priority","Status","Due"].map((h,i) => (
                                      <th key={i} style={{ fontSize:"0.65rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em", paddingBottom:8, whiteSpace:"nowrap" }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {myOpenTasks.slice(0,8).map(t => {
                                    const sb  = STATUS_BADGE[t.status]     || { bg:T.bg, color:T.text2 };
                                    const pb  = PRIORITY_BADGE[t.priority] || { bg:T.bg, color:T.text2 };
                                    const due = t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short"}) : "—";
                                    const isOvr = t.dueDate && new Date(t.dueDate) < today;
                                    return (
                                      <tr key={t.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                                        <td>
                                          <a href={`${window.location.origin}/tasks?taskId=${t.id}`} target="_blank" rel="noopener noreferrer"
                                            style={{ color:T.primary, fontWeight:700, textDecoration:"none", fontSize:"0.76rem", display:"block" }}>
                                            {t.taskCode||`#${t.id}`}
                                          </a>
                                          <div style={{ fontSize:"0.78rem", color:T.text1, fontWeight:500, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.title}</div>
                                        </td>
                                        <td style={{ fontSize:"0.75rem", color:T.text2, whiteSpace:"nowrap" }}>{t.projectName||"—"}</td>
                                        <td><span style={{ ...pb, borderRadius:20, padding:"2px 9px", fontSize:"0.7rem", fontWeight:700 }}>{t.priority||"—"}</span></td>
                                        <td><span style={{ ...sb, borderRadius:20, padding:"2px 9px", fontSize:"0.7rem", fontWeight:600, whiteSpace:"nowrap" }}>{(t.status||"").replace(/_/g," ")}</span></td>
                                        <td><span style={{ fontSize:"0.78rem", fontWeight:600, color:isOvr?"#dc2626":T.text2, whiteSpace:"nowrap" }}>{due}</span></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              {myOpenTasks.length>8 && (
                                <div style={{ textAlign:"center", paddingTop:12 }}>
                                  <Link to="/viewtasks" style={{ fontSize:"0.78rem", color:T.primary, fontWeight:600, textDecoration:"none" }}>
                                    +{myOpenTasks.length-8} more → View all
                                  </Link>
                                </div>
                              )}
                            </div>
                          )
                        }
                      </Card>
                    </div>
                  </div>

                  {/* Aging notice */}
                  {D.agingCount > 0 && (
                    <div style={{ marginTop:16, padding:"14px 20px", background:"#fef2f2", borderRadius:T.radius, border:"1px solid #fecaca", display:"flex", alignItems:"center", gap:12 }}>
                      <FaExclamationTriangle color="#dc2626" size={16} />
                      <span style={{ fontSize:"0.84rem", color:"#dc2626", fontWeight:600 }}>
                        {D.agingCount} of your open {D.agingCount===1?"task":"tasks"} ha{D.agingCount===1?"s":"ve"} been open for over 30 days.
                      </span>
                      <Link to="/viewtasks" style={{ fontSize:"0.78rem", color:"#dc2626", fontWeight:700, textDecoration:"underline", marginLeft:"auto", whiteSpace:"nowrap" }}>
                        Review now →
                      </Link>
                    </div>
                  )}
                </>
              )}

              {/* ── WEEKLY TRENDS ── */}
              {activeDashTab === "Weekly Trends" && (
                <>
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {["All", ...D.allTypes].map(tp => (
                      <button key={tp} onClick={() => setWeekTypeFilter(tp)} style={{
                        padding:"4px 14px", fontSize:"0.78rem", borderRadius:6, cursor:"pointer",
                        border:`1.5px solid ${weekTypeFilter===tp?T.primary:T.border}`,
                        background:weekTypeFilter===tp?T.primary:T.white,
                        color:weekTypeFilter===tp?T.white:T.text2,
                        fontWeight:weekTypeFilter===tp?600:500, transition:"all 0.15s",
                      }}>{tp}</button>
                    ))}
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <Card>
                        <CardHead label="My Weekly Category Breakdown" />
                        <ResponsiveContainer width="100%" height={230}>
                          <BarChart data={filteredWeeklyByType} margin={{ top:0, right:8, left:-24, bottom:0 }} barSize={9}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                            <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} />
                            <Legend wrapperStyle={{ fontSize:"0.72rem", paddingTop:10, color:T.text2 }} iconType="circle" iconSize={7} />
                            {(weekTypeFilter==="All"?D.allTypes:[weekTypeFilter]).map((tp,i) => (
                              <Bar key={tp} dataKey={tp} stackId="a"
                                fill={TYPE_COLORS[D.allTypes.indexOf(tp)%TYPE_COLORS.length]}
                                radius={i===(weekTypeFilter==="All"?D.allTypes:[weekTypeFilter]).length-1?[2,2,0,0]:[0,0,0,0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                    <div className="col-12 col-md-6">
                      <Card>
                        <CardHead label="My Weekly by Priority" />
                        <ResponsiveContainer width="100%" height={230}>
                          <BarChart data={D.weeklyByPriority} margin={{ top:0, right:8, left:-24, bottom:0 }} barSize={9}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                            <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} />
                            <Legend wrapperStyle={{ fontSize:"0.72rem", paddingTop:10, color:T.text2 }} iconType="circle" iconSize={7} />
                            <Bar dataKey="High"   stackId="p" fill="#ef4444" radius={[0,0,0,0]} />
                            <Bar dataKey="Medium" stackId="p" fill="#f59e0b" radius={[0,0,0,0]} />
                            <Bar dataKey="Low"    stackId="p" fill="#10b981" radius={[2,2,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                    <div className="col-12">
                      <Card>
                        <CardHead label="My Running Volume (14 weeks)"
                          right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>tasks assigned per week</span>}
                        />
                        <ResponsiveContainer width="100%" height={155}>
                          <AreaChart data={D.weeklyData} margin={{ top:4, right:8, left:-24, bottom:0 }}>
                            <defs>
                              <linearGradient id="empGrad2" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor={T.primary} stopOpacity={0.14} />
                                <stop offset="95%" stopColor={T.primary} stopOpacity={0}    />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} />
                            <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} />
                            <Area type="monotone" dataKey="tasks" name="My Tasks" stroke={T.primary} strokeWidth={2}
                              fill="url(#empGrad2)" dot={{ fill:T.primary, r:3, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                  </div>
                </>
              )}

            </div>
          )}
        </div>
      </div>

      <style>{`
        .emp-scroll::-webkit-scrollbar { width: 6px; }
        .emp-scroll::-webkit-scrollbar-track { background: transparent; }
        .emp-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .emp-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .emp-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
      `}</style>
    </>
  );
};

export default EmployeeTaskLanding;
